import React, { useEffect, useState } from "react";
import WithdrawalRequestsTable from "./WithdrawalRequestsTable";
import RejectionModal from "../DepositRequests/RejectionModal";
import WithdrawalService from "service/WithdrawalService";

function OperatorWithdrawalRequests() {
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [rejectionReason, setRejectionReason] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [screenshot, setScreenshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
const [screenshots, setScreenshots] = useState({});

  const withdrawalService = new WithdrawalService();

  const fetchAll = async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const res = await withdrawalService.fetchWithdrawals({ items_per_page: 100 });
      const rows = Array.isArray(res?.data) ? res.data : res?.data?.items || [];
      setWithdrawalRequests(rows);
    } catch (e) {
      setErrorMessage(e?.response?.data?.message || e?.message || "Failed to fetch withdrawals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleAllow = async (id) => {
    try {
      setProcessing(true);
      setErrorMessage(""); setSuccessMessage("");
      await withdrawalService.allowWithdrawal(id);
      await fetchAll();
      setSuccessMessage("Withdrawal allowed.");
    } catch (e) {
      setErrorMessage(e?.response?.data?.message || e?.message || "Failed to allow withdrawal");
    } finally {
      setProcessing(false);
    }
  };

  const handleApprove = async (id) => {
    const file = screenshots[id];
    if (!file) {
      setErrorMessage("Please upload payment proof screenshot before approving");
      return;
    }
    try {
      setProcessing(true);
      setErrorMessage(""); setSuccessMessage("");
      await withdrawalService.approveWithdrawal(
        id,
        { adminNotes: "Approved by operator" },
        file
      );
      setScreenshots(prev => {
        const { [id]: _removed, ...rest } = prev;
        return rest;
      });
      await fetchAll();
      setSuccessMessage("Withdrawal approved.");
    } catch (e) {
      setErrorMessage(e?.response?.data?.message || e?.message || "Failed to approve withdrawal");
    } finally {
      setProcessing(false);
    }
  };


  const handleReject = async (id) => {
    if (!rejectionReason.trim()) {
      setErrorMessage("Please enter a rejection reason");
      return;
    }
    try {
      setProcessing(true);
      setErrorMessage(""); setSuccessMessage("");
      await withdrawalService.rejectWithdrawal(id, rejectionReason, "Rejected by operator");
      setRejectionReason("");
      setSelectedRequest(null);
      await fetchAll();
      setSuccessMessage("Withdrawal rejected.");
    } catch (e) {
      setErrorMessage(e?.response?.data?.message || e?.message || "Failed to reject withdrawal");
    } finally {
      setProcessing(false);
    }
  };

  const openRejectModal = (request) => { setSelectedRequest(request); setRejectionReason(""); };
  const closeRejectModal = () => { setSelectedRequest(null); setRejectionReason(""); };

  const pendingRequests = withdrawalRequests.filter(r => r.status === "pending");
  const allowedRequests = withdrawalRequests.filter(r => r.status === "allowed");
  const processedRequests = withdrawalRequests.filter(r => r.status === "approved" || r.status === "rejected");

  if (loading) {
    return (
      <div className="text-slate-800 p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p>Loading withdrawal requests...</p>
      </div>
    );
  }

  return (
    <div className="text-slate-800 md:px-5 md:py-4 p-2 bg-white max-w-screen-lg mx-auto">
      <h1 className="md:text-[26px] text-[20px] font-semibold md:mb-6 mb-4 text-center md:text-left">
        Withdrawal Requests
      </h1>

      {errorMessage && (
        <div className="bg-red-50 border border-red-400 text-red-600 px-4 py-3 rounded-lg mb-6">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-500 text-green-700 px-4 py-3 rounded-lg mb-6">
          {successMessage}
        </div>
      )}

      {/* Pending */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-amber-600">
          Pending Requests ({pendingRequests.length})
        </h2>
        <WithdrawalRequestsTable
          requests={pendingRequests}
          onAllow={handleAllow}                          
          onReject={(r) => openRejectModal(r)}         
          section="pending"
        />
      </div>

      {/* Allowed */}
   <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-blue-600">
          Allowed Requests ({allowedRequests.length})
        </h2>
        <WithdrawalRequestsTable
          requests={allowedRequests}
          onApprove={handleApprove}
          onReject={(r) => openRejectModal(r)}
          // pass the map + a setter that knows the id
          screenshotMap={screenshots}
          onScreenshotChange={(id, file) =>
            setScreenshots(prev => ({ ...prev, [id]: file || undefined }))
          }
          section="allowed"
        />
        {processing && <p className="text-sm text-slate-500 mt-2">Processing...</p>}
      </div>

      {/* Processed */}
      <div>
        <h2 className="text-xl font-semibold mb-4 text-slate-600">
          Processed Requests ({processedRequests.length})
        </h2>
        <WithdrawalRequestsTable
          requests={processedRequests}
          section="processed"
        />
      </div>

      {/* Rejection Modal */}
      <RejectionModal
        isOpen={!!selectedRequest}
        request={selectedRequest}
        rejectionReason={rejectionReason}
        onRejectionReasonChange={setRejectionReason}
        onConfirmReject={() => handleReject(selectedRequest?._id || selectedRequest?.id)}
        onCancel={closeRejectModal}
        type="withdrawal"
      />
    </div>
  );
}

export default OperatorWithdrawalRequests;
